import React, {useCallback, useEffect, useState} from 'react';
import {requestDashboard} from '../../../api/api';
import {PaginationResult, Reservation} from '../../dashboard/types';
import {ReservationDetailModal} from '../Modals';
import {getStatusBadge} from '../../ui/ReservationStatusBadge';

export function EventReservations({apiBase, token, eventId, title, onBack}: {apiBase: string; token: string; eventId: string; title: string; onBack: () => void}) {
  const [data, setData] = useState<PaginationResult<Reservation> | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [drafts, setDrafts] = useState<Record<string, Partial<Reservation>>>({});
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {setData(await requestDashboard<PaginationResult<Reservation>>({apiBase, token, path: `/reservations/events/${eventId}?page=${page}`, withAuth: true}));}
    catch (e) {setError(e instanceof Error ? e.message : 'Unable to load event reservations.');}
    finally {setLoading(false);}
  }, [apiBase, token, eventId, page]);
  useEffect(() => {void load();}, [load]);
  const selected = data?.items.find(r => r.id === selectedId) || null;
  const save = async (id: string) => {
    if (saving) return false;
    setSaving(true); setError('');
    try {
      const draft = drafts[id] || {};
      await requestDashboard({apiBase, token, path: `/reservations/${id}`, withAuth: true, init: {method: 'PATCH', body: JSON.stringify({status: draft.status, notes: draft.notes})}});
      setDrafts({}); await load(); return true;
    } catch (e) {setError(e instanceof Error ? e.message : 'Unable to update reservation.'); return false;}
    finally {setSaving(false);}
  };
  return <section className='events-panel'>
    <button type='button' onClick={onBack}>Back to events</button>
    <div className='events-heading'><div><h2>{title} — reservations</h2><p>{data?.totalItems ?? 0} event reservations. Normal reservations are listed separately.</p></div><button type='button' disabled={loading} onClick={load}>Refresh</button></div>
    {error && !selected && <p role='alert' className='events-error'>{error}</p>}
    {loading && <p role='status'>Loading reservations…</p>}
    {!loading && !data?.items.length && <p>No reservations for this event yet.</p>}
    {!!data?.items.length && <div className='event-reservations-table'><table>
      <thead><tr><th>Customer</th><th>Section</th><th>Guests</th><th>Event total</th><th>Status</th><th>Payment</th><th>Details</th></tr></thead>
      <tbody>{data.items.map(reservation => <tr key={reservation.id}>
        <td><strong>{reservation.customerName}</strong><br/>{reservation.customerEmail}<br/>{reservation.customerPhone}</td>
        <td>{reservation.table?.section?.name || '—'}</td><td>{reservation.numberOfGuests}</td>
        <td>{reservation.depositCurrency || 'TZS'} {Number(reservation.depositAmount || 0).toLocaleString()}</td>
        <td>{getStatusBadge(reservation.status)}</td><td>{reservation.paymentStatus === 'paid' ? 'Confirmed' : reservation.paymentStatus?.replace(/_/g, ' ') || '—'}</td>
        <td><button type='button' onClick={() => {setSelectedId(reservation.id); setDrafts({});}}>View reservation</button></td>
      </tr>)}</tbody>
    </table></div>}
    <div className='events-actions'><button type='button' disabled={loading || page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button><span>Page {page} of {Math.max(1, data?.totalPages || 1)}</span><button type='button' disabled={loading || !data?.hasNextPage} onClick={() => setPage(p => p + 1)}>Next</button></div>
    <ReservationDetailModal saveError={error} apiBase={apiBase} token={token} canViewProofs selectedReservation={selected} reservationDrafts={drafts} updateReservationDraft={(id, key, value) => setDrafts(current => ({...current, [id]: {...current[id], [key]: value}}))} tables={[]} onClose={() => setSelectedId(undefined)} onSave={save} canEdit={!saving}/>
  </section>;
}
